import { subject } from "@casl/ability";
import { useAbility } from "@casl/react";
import { Mail } from "lucide-react";
import type { FC } from "react";

import type { AppAbility } from "@recobra/core";
import { m } from "@recobra/i18n/messages";
import { OPEN_TASK_STATUSES, type Task, type TaskEmail } from "@recobra/schemas/task";
import { Button } from "@recobra/ui/button";
import { Item, ItemActions, ItemContent, ItemDescription } from "@recobra/ui/item";

import { TaskModalSection, formatTaskTimestamp } from "../task-display";
import { useEmailTaskAssignee } from "./useEmailTaskAssignee";

export const TaskAssigneeEmailSection: FC<{ task: Task }> = ({ task }) => {
  const ability = useAbility<AppAbility>();
  const send = useEmailTaskAssignee(task);
  const canEmail =
    ability.can("emailAssignee", subject("Task", task)) &&
    OPEN_TASK_STATUSES.includes(task.status) &&
    Boolean(task.assignedTo);
  if (!canEmail) return null;

  const latest = task.emails.findLast((email) => email.recipientUsername === task.assignedTo);
  const status = emailStatus(send.isPending, latest);

  return (
    <TaskModalSection>
      <Item size="xs">
        <ItemContent>{status ? <ItemDescription>{status}</ItemDescription> : null}</ItemContent>
        <ItemActions>
          <Button
            type="button"
            size="xs"
            variant="outline"
            disabled={send.isPending}
            onClick={() => send.mutate()}
          >
            <Mail data-icon="inline-start" />
            {latest ? m.task_email_resend() : m.task_email_send()}
          </Button>
        </ItemActions>
      </Item>
    </TaskModalSection>
  );
};

const emailStatus = (sending: boolean, latest: TaskEmail | undefined): string | undefined => {
  if (sending) return m.task_email_sending();
  return latest ? EMAIL_STATUS_LABELS[latest.status](latest) : undefined;
};

const EMAIL_STATUS_LABELS: Record<TaskEmail["status"], (email: TaskEmail) => string> = {
  sent: (email) => m.task_email_sent({ date: formatTaskTimestamp(email.createdAt) }),
  skipped: () => m.task_email_skipped(),
  bounced: () => m.task_email_bounced(),
  complained: () => m.task_email_complained(),
};
